import { store, RootState } from '../store';
import { OrderingConstants, OrderingSelectors, OrderingUtils } from 'polygon-ordering';
import moment from 'moment-timezone';
import { GIFTCARD_PURCHASE_SUCCESSFUL_MODAL } from '../modals/GiftcardSuccessModal';

const { getRememberCreditCard, getCvcToken, getGatewayType } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES, PAYMENT_METHOD } = OrderingConstants;
const { dollarsToCents } = OrderingUtils;

const getPaymentInfoBody = (
  gatewayType: string,
  paymentMethod: SubPayment | null,
  token: any,
  savedCardToken: string | null,
  cvcToken: string | null,
  profile: Profile | null,
  rememberCreditCard: boolean,
) => {
  if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE) {
    return {
      ...(paymentMethod?.method === PAYMENT_METHOD.SAVED_CARD
        ? { saved_pg_token: savedCardToken, cardCvcToken: cvcToken }
        : { id: token!.id }),
      remember_pg_token: profile ? rememberCreditCard || undefined : false, // false for giftcards
      last_four: rememberCreditCard ? token?.card?.last4 : undefined,
      useIntents: true,
      paymentMethodType: paymentMethod?.method,
    };
  }

  if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
    if (paymentMethod?.method === PAYMENT_METHOD.SAVED_CARD) {
      return { pay_method_id: savedCardToken };
    } else if (paymentMethod?.method === PAYMENT_METHOD.CREDIT_CARD) {
      return { save_pay_method: profile && rememberCreditCard };
    }
    return {};
  }
};

export default function constructGiftCardTopUpPaymentRequest(data: ProcessPaymentData) {
  const gatewayType = getGatewayType(store.getState() as RootState);
  const paymentMethod = (store.getState() as RootState).paymentMethod;
  const savedCardToken = (store.getState() as RootState).savedCardToken;
  const rememberCreditCard = getRememberCreditCard(store.getState() as RootState);
  const profile = (store.getState() as RootState).profile;
  const cvcToken = getCvcToken(store.getState() as RootState);
  const googleReCaptcha = (store.getState() as RootState).googleReCaptcha;
  const enableReCaptchaForMemberTopUp = (store.getState() as RootState).config
    .enableReCaptchaForMemberTopUp;
  const enableReCaptchaForGiftCard = (store.getState() as RootState).config
    .enableReCaptchaForGiftCard;

  const { route, token, values } = data;

  let path: string = '';
  let modal: string = '';
  let body: PaymentPacket = {
    PaymentType: 'cc', // This will always be cc as for gift card/ top up we can only use credit card, google pay, apple pay and saved cards
    PaymentInfo: getPaymentInfoBody(
      gatewayType,
      paymentMethod,
      token,
      savedCardToken,
      cvcToken,
      profile,
      rememberCreditCard,
    ),
    'g-recaptcha-response': googleReCaptcha,
  };

  if (route === 'memberPurchaseGiftCardForSelf') {
    path =
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
        ? '/api/v1/sale/giftcard/token_sale'
        : '/api/v1/sale/giftcard';
    body.OrderTotal = dollarsToCents(values.Amount!);
    body.Email = profile?.emailAddress;
    body.ConfirmEmail = profile?.emailAddress;
    body.Surname = profile?.surname;
    body.GivenNames = profile?.givenNames;
    body.Surcharge = { Amount: data.giftcardSurcharge!, Type: 11 };
    if (!enableReCaptchaForGiftCard) body['g-recaptcha-response'] = null;

    modal = GIFTCARD_PURCHASE_SUCCESSFUL_MODAL;
  }

  if (route === 'nonMemberPurchaseGiftCardForSelf') {
    path =
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
        ? '/api/v1/sale/giftcard/token_sale'
        : '/api/v1/sale/giftcard';
    body.OrderTotal = dollarsToCents(values.Amount!);
    body.Email = values.Email;
    body.ConfirmEmail = values.ConfirmEmail;
    body.Surname = values.LastName;
    body.GivenNames = values.FirstName;
    body.Surcharge = { Amount: data.giftcardSurcharge!, Type: 11 };
    if (!enableReCaptchaForGiftCard) body['g-recaptcha-response'] = null;

    modal = GIFTCARD_PURCHASE_SUCCESSFUL_MODAL;
  }

  if (route === 'memberPurchaseGiftCardForOthers') {
    path =
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
        ? '/api/v1/sale/giftcard/token_sale'
        : '/api/v1/sale/giftcard';
    body.Email = profile?.emailAddress;
    body.ConfirmEmail = profile?.emailAddress;
    body.Surname = profile?.surname;
    body.GivenNames = profile?.givenNames;

    body.RecipientEmail = values.RecipientEmail;
    body.RecipientName = values.RecipientName;
    body.RecipientSurname = values.RecipientSurname;
    body.Memo = values.Memo;
    body.OrderTotal = dollarsToCents(values.Amount!);
    body.Surcharge = { Amount: data.giftcardSurcharge!, Type: 11 };

    if (values.SendTime !== '' && values.delivery) {
      body.SendTime = moment(values.SendTime).tz(values.timezone!, true).toISOString();
    }
    if (!enableReCaptchaForGiftCard) body['g-recaptcha-response'] = null;
    modal = GIFTCARD_PURCHASE_SUCCESSFUL_MODAL;
  }

  if (route === 'nonMemberPurchaseGiftCardForOthers') {
    path =
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
        ? '/api/v1/sale/giftcard/token_sale'
        : '/api/v1/sale/giftcard';
    body.RecipientEmail = values.RecipientEmail;
    body.RecipientName = values.RecipientName;
    body.RecipientSurname = values.RecipientSurname;
    body.Memo = values.Memo;
    body.OrderTotal = dollarsToCents(values.Amount!);

    body.Email = values.Email;
    body.ConfirmEmail = values.ConfirmEmail;
    body.Surname = values.LastName;
    body.GivenNames = values.FirstName;
    body.Surcharge = { Amount: data.giftcardSurcharge!, Type: 11 };

    if (values.SendTime !== '' && values.delivery) {
      body.SendTime = moment(values.SendTime).tz(values.timezone!, true).toISOString();
    }
    if (!enableReCaptchaForGiftCard) body['g-recaptcha-response'] = null;
    modal = GIFTCARD_PURCHASE_SUCCESSFUL_MODAL;
  }

  if (route === 'memberCashTopUp') {
    path =
      gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? 'api/v1/sale/topup/token_sale' : '/api/v1/sale';
    body.HCTopupAmount = dollarsToCents(values.Amount!);
    if (!enableReCaptchaForMemberTopUp) body['g-recaptcha-response'] = null;
  }

  if (route === 'giftCardTopUp') {
    path = '/api/v1/sale/nonmember';

    if (profile) {
      path = '/api/v1/sale';
    }

    if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO) {
      path = 'api/v1/sale/topup/token_sale';
    }
    
    body.GCMemberNo = values.MemberNo;
    body.GCTopupAmount = dollarsToCents(Number(values.Amount!));
    body.VerifyPin = values.VerifyPIN;
    if (!enableReCaptchaForGiftCard) body['g-recaptcha-response'] = null;
  }

  return {
    body,
    path,
    modal,
  };
}
